// GameState — all mutable game state and its pure logic. No engine imports, so
// `verify.ts` exercises the whole loop headlessly (inventory, crafting, quality,
// meters, requests, chapters, workshop levels).
import {
  STRENGTH_MAX, PEACE_MAX, WORKSHOP_THRESHOLDS, RESTORE, COST,
  CH2_REQUESTS, CH3_FAMILY, CH3_HOME_PROJECTS, COMMUNITY_STAGES,
  WORKBENCH_COST,
} from './config.js';
import {
  RECIPES, REQUESTS, recipeById, requestById, MATERIALS, COMMUNITY_PROJECT,
  type MaterialKey, type Recipe, type Request,
} from './data.js';

export interface Product { recipe: string; quality: number; } // quality 0..2

export type RequestStatus = 'locked' | 'offered' | 'accepted' | 'done';

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Map a 0..1 mini-game score to a quality index (0 Good, 1 Excellent, 2 Masterwork). */
export function qualityFromScore(score: number): number {
  if (score >= 0.85) return 2;
  if (score >= 0.55) return 1;
  return 0;
}

/** Craftsmanship → workshop level 1..7 (level 0 until the workbench is built). */
export function levelForCraftsmanship(c: number): number {
  let lvl = 1;
  for (let i = 0; i < WORKSHOP_THRESHOLDS.length; i++) {
    if (c >= WORKSHOP_THRESHOLDS[i]) lvl = i + 1;
  }
  return lvl;
}

export class GameState {
  materials: Record<MaterialKey, number> = { wood: 0, timber: 0, stone: 0, rope: 0, cloth: 0, peg: 0 };
  products: Product[] = [];

  strength = STRENGTH_MAX;
  peace = 80;

  craftsmanship = 0;
  charity = 0;
  family = 0;

  day = 1;
  chapter = 1;

  workbenchBuilt = false;
  gotMasterwork = false;
  homeProjects = 0;
  requestsDone = 0;

  requests: Record<string, RequestStatus> = {};
  codexFound: Set<string> = new Set();
  freePlay = false;

  communityStage = 0; // 0..4 (4 == complete)

  constructor() {
    for (const r of REQUESTS) this.requests[r.id] = 'locked';
  }

  // ── materials ──────────────────────────────────────────────────────────────
  add(key: MaterialKey, n = 1): void { this.materials[key] += n; }

  has(cost: Partial<Record<MaterialKey, number>>): boolean {
    for (const k in cost) {
      const key = k as MaterialKey;
      if (this.materials[key] < (cost[key] ?? 0)) return false;
    }
    return true;
  }

  spend(cost: Partial<Record<MaterialKey, number>>): boolean {
    if (!this.has(cost)) return false;
    for (const k in cost) {
      const key = k as MaterialKey;
      this.materials[key] -= cost[key] ?? 0;
    }
    return true;
  }

  totalMaterials(): number {
    let t = 0;
    for (const k in this.materials) t += this.materials[k as MaterialKey];
    return t;
  }

  // ── products ────────────────────────────────────────────────────────────────
  hasProduct(recipe: string): boolean { return this.products.some((p) => p.recipe === recipe); }

  removeProduct(recipe: string): Product | null {
    // remove the highest-quality matching product
    let best = -1, bi = -1;
    for (let i = 0; i < this.products.length; i++) {
      if (this.products[i].recipe === recipe && this.products[i].quality > best) { best = this.products[i].quality; bi = i; }
    }
    if (bi < 0) return null;
    return this.products.splice(bi, 1)[0];
  }

  // ── crafting ─────────────────────────────────────────────────────────────────
  canCraft(recipe: Recipe): boolean {
    return this.workbenchBuilt && this.workshopLevel() >= recipe.minLevel && this.has(recipe.cost) && this.strength > 0;
  }

  /** Complete a craft: consumes materials, adds a product of the given quality,
   *  awards craftsmanship (scaled by quality) and spends strength. Returns the product. */
  craft(recipe: Recipe, quality: number): Product | null {
    if (!this.spend(recipe.cost)) return null;
    const q = clamp(Math.round(quality), 0, 2);
    const product: Product = { recipe: recipe.id, quality: q };
    if (recipe.yields) {
      // An INTERMEDIATE GOOD. It becomes materials on the shelf, not a
      // deliverable product — pegs are for building with, not for giving away.
      for (const k in recipe.yields) this.add(k as MaterialKey, recipe.yields[k as MaterialKey] ?? 0);
    } else {
      this.products.push(product);
    }
    const bonus = [1, 1.35, 1.8][q];
    this.gainCraftsmanship(Math.round(recipe.craftsmanship * bonus));
    this.addStrength(-(recipe.major ? COST.craftMajor : COST.craftMinor));
    if (q === 2) this.gotMasterwork = true;
    return product;
  }

  buildWorkbench(): boolean {
    if (this.workbenchBuilt) return false;
    if (!this.spend({ ...WORKBENCH_COST })) return false;
    this.workbenchBuilt = true;
    this.addStrength(-COST.craftMajor);
    this.gainCraftsmanship(8);
    return true;
  }

  workshopLevel(): number {
    return this.workbenchBuilt ? levelForCraftsmanship(this.craftsmanship) : 0;
  }

  // ── meters & progress ────────────────────────────────────────────────────────
  addStrength(n: number): void { this.strength = clamp(this.strength + n, 0, STRENGTH_MAX); }
  addPeace(n: number): void { this.peace = clamp(this.peace + n, 0, PEACE_MAX); }
  gainCraftsmanship(n: number): void { this.craftsmanship += n; }
  gainCharity(n: number): void { this.charity += n; }
  gainFamily(n: number): void { this.family += n; }

  eatMeal(): void { this.addStrength(RESTORE.meal); this.addPeace(RESTORE.mealPeace); this.gainFamily(1); }
  pray(): void { this.addPeace(RESTORE.prayerPeace); }
  drinkWater(): void { this.addStrength(RESTORE.water); }
  rest(): void { this.addStrength(RESTORE.rest); this.addPeace(4); }

  /** High peace makes Joseph work a touch more efficiently (atmospheric). */
  efficiency(): number { return this.peace >= 70 ? 1.15 : this.peace <= 20 ? 0.9 : 1; }

  // ── requests ─────────────────────────────────────────────────────────────────
  refreshOffered(): void {
    for (const r of REQUESTS) {
      if (this.requests[r.id] === 'locked' && r.chapter <= this.chapter) {
        this.requests[r.id] = 'offered';
      }
    }
  }

  acceptRequest(id: string): boolean {
    if (this.requests[id] !== 'offered') return false;
    this.requests[id] = 'accepted';
    return true;
  }

  /** Deliver an accepted request's product. `giveFree` grants extra charity. */
  deliverRequest(id: string, giveFree = false): boolean {
    const req = requestById(id);
    if (!req || this.requests[id] !== 'accepted') return false;
    if (!this.removeProduct(req.need)) return false;
    this.requests[id] = 'done';
    this.gainCraftsmanship(req.craftsmanship);
    this.gainCharity(req.charity + (giveFree && req.canGiveFree ? 4 : 0));
    this.addPeace(RESTORE.helpPeace);
    this.requestsDone++;
    if (req.home) this.homeProjects++;
    return true;
  }

  /** Has this request been delivered? Drives the furniture Joseph has actually
   *  built appearing in his own home — the payoff the whole loop is aimed at. */
  requestDone(id: string): boolean { return this.requests[id] === 'done'; }

  activeRequests(): Request[] { return REQUESTS.filter((r) => this.requests[r.id] === 'accepted'); }
  offeredRequestFor(giver: string): Request | undefined {
    return REQUESTS.find((r) => r.giver === giver && this.requests[r.id] === 'offered');
  }
  deliverableRequestFor(giver: string): Request | undefined {
    return REQUESTS.find((r) => r.giver === giver && this.requests[r.id] === 'accepted' && this.hasProduct(r.need));
  }

  // ── community project ─────────────────────────────────────────────────────────
  canBuildCommunityStage(): boolean {
    if (this.communityStage >= COMMUNITY_STAGES) return false;
    return this.has(COMMUNITY_PROJECT.stages[this.communityStage].cost);
  }

  buildCommunityStage(): boolean {
    if (!this.canBuildCommunityStage()) return false;
    this.spend(COMMUNITY_PROJECT.stages[this.communityStage].cost);
    this.communityStage++;
    this.gainCraftsmanship(20);
    this.gainCharity(4);
    this.addPeace(10);
    return true;
  }

  communityComplete(): boolean { return this.communityStage >= COMMUNITY_STAGES; }

  // ── chapters ──────────────────────────────────────────────────────────────────
  /** If the current chapter's goal is met, advance and return the new chapter number. */
  advanceChapterIfReady(): number | null {
    let advanced = false;
    if (this.chapter === 1 && this.workbenchBuilt) { this.chapter = 2; advanced = true; }
    else if (this.chapter === 2 && this.requestsDone >= CH2_REQUESTS) { this.chapter = 3; advanced = true; }
    else if (this.chapter === 3 && this.family >= CH3_FAMILY && this.homeProjects >= CH3_HOME_PROJECTS) { this.chapter = 4; advanced = true; }
    else if (this.chapter === 4 && this.gotMasterwork) { this.chapter = 5; advanced = true; }
    if (advanced) this.refreshOffered();
    return advanced ? this.chapter : null;
  }

  /** A short human-readable line describing progress toward the current goal. */
  goalProgress(): string {
    switch (this.chapter) {
      case 1: return this.workbenchBuilt ? 'Workbench built!' : 'Gather 6 Wood + 2 Stone, then build at the workshop';
      case 2: return `Village requests: ${Math.min(this.requestsDone, CH2_REQUESTS)}/${CH2_REQUESTS}`;
      case 3: return `Family ${Math.min(this.family, CH3_FAMILY)}/${CH3_FAMILY} · Home projects ${this.homeProjects}/${CH3_HOME_PROJECTS}`;
      case 4: return this.gotMasterwork ? 'Masterwork complete!' : 'Craft a piece at Masterwork quality';
      case 5: return this.communityComplete() ? 'The canopy is finished' : `Canopy: ${this.communityStage}/${COMMUNITY_STAGES} stages`;
      default: return '';
    }
  }

  // ── new day ───────────────────────────────────────────────────────────────────
  sleep(): void {
    this.day++;
    this.addStrength(STRENGTH_MAX); // wake rested
    this.addPeace(10);
    this.refreshOffered();
  }

  /** The 2–4 gentle suggested tasks shown at the start of a day. */
  dailyTasks(): string[] {
    const tasks: string[] = [];
    if (this.chapter === 1 && !this.workbenchBuilt) {
      tasks.push('Gather Wood in the Grove', 'Gather Stone in the Hills', 'Build your workbench');
    } else {
      for (const r of this.activeRequests()) tasks.push(this.hasProduct(r.need) ? `Deliver: ${r.title}` : r.title);
      if (tasks.length < 2) {
        const offered = REQUESTS.find((rr) => this.requests[rr.id] === 'offered');
        if (offered) tasks.push(`Speak with a neighbor in the village`);
      }
      tasks.push('Help at home');
      if (this.chapter === 5 && !this.communityComplete()) tasks.push('Work on the well canopy');
    }
    return tasks.slice(0, 4);
  }
}

// Small helpers used by both logic and UI.
export function materialLabel(key: MaterialKey): string { return MATERIALS[key].name; }
export function recipesUpTo(level: number): Recipe[] { return RECIPES.filter((r) => r.minLevel <= level); }
